import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, Search, ChevronRight, Loader2, Building2, Calendar,
  Plus, X, User, Edit2, UserCheck, Users, Shield, Columns,
} from "lucide-react";
import { toast } from "sonner";
import api, { crmApi, formatError, hasCapability, userPrefsApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLabel } from "@/contexts/LabelContext";

const PAGE_SIZE = 25;
const inp = "w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] focus:outline-none focus:border-[#0176D3]/50 focus:ring-1 focus:ring-[#0176D3]/20";

// CRM-UX-01 Stage 03 — Opportunities column registry
const OPPS_COL_REGISTRY = [
  { id: "name",            label: "Opportunity",   defaultVisible: true,  alwaysVisible: false },
  { id: "account_name",    label: "Account",       defaultVisible: true,  alwaysVisible: false },
  { id: "contact",         label: "Contact",       defaultVisible: true,  alwaysVisible: false },
  { id: "stage",           label: "Stage",         defaultVisible: true,  alwaysVisible: true  },
  { id: "owner",           label: "Owner",         defaultVisible: true,  alwaysVisible: false },
  { id: "budget_range",    label: "Budget",        defaultVisible: true,  alwaysVisible: false },
  { id: "event_type",      label: "Event Type",    defaultVisible: false, alwaysVisible: false },
  { id: "event_date",      label: "Event Date",    defaultVisible: false, alwaysVisible: false },
  { id: "estimated_value", label: "Est. Value",    defaultVisible: false, alwaysVisible: false },
  { id: "source_lead",     label: "Source Lead",   defaultVisible: false, alwaysVisible: false },
];
const DEFAULT_OPPS_COLS = OPPS_COL_REGISTRY.filter(c => c.defaultVisible || c.alwaysVisible).map(c => c.id);

const STAGE_COLORS = {
  PROSPECTING:   "text-[#706E6B] bg-slate-400/10 border-slate-400/20",
  QUALIFICATION: "text-sky-400 bg-sky-400/10 border-sky-400/20",
  PROPOSAL:      "text-[#0176D3] bg-blue-400/10 border-blue-400/20",
  NEGOTIATION:   "text-[#5E4DB2] bg-[#F1ECFF] border-[#D8CEF7]",
  CLOSED_WON:    "text-[#2E844A] bg-emerald-400/10 border-emerald-400/20",
  CLOSED_LOST:   "text-rose-400 bg-rose-400/10 border-rose-400/20",
};

const STAGES = [
  "RFP", "TECHNICAL_REVIEW", "PROSPECTING", "QUALIFICATION",
  "PROPOSAL", "NEGOTIATION", "CONTRACT", "CLOSED_WON", "CLOSED_LOST",
];
const PROPOSAL_STAGES = new Set(["PROPOSAL", "NEGOTIATION", "CONTRACT", "CLOSED_WON"]);
const CLOSED_STAGES   = new Set(["CLOSED_WON", "CLOSED_LOST"]);

// ── Contact picker: load contacts for a given account ────────────────────────
function useAccountContacts(accountId) {
  const [contacts, setContacts] = useState([]);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (!accountId) { setContacts([]); return; }
    setLoading(true);
    api.get("/crm/contacts", { params: { account_id: accountId, limit: 100 } })
      .then(r => setContacts(r.data.results || []))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  }, [accountId]);

  return { contacts, loading };
}

// ── Create Opportunity Modal ─────────────────────────────────────────────────
function CreateOpportunityModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", account_id: "", contact_id: "", stage: "PROSPECTING",
    event_type: "", event_date: "", estimated_value: "", close_reason: "", notes: "",
  });
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [accounts, setAccounts] = useState([]);

  const { contacts, loading: contactsLoading } = useAccountContacts(form.account_id);

  useEffect(() => {
    api.get("/crm/accounts", { params: { limit: 100 } })
      .then(r => setAccounts(r.data.results || []))
      .catch(() => {});
  }, []);

  const set = (k, v) => {
    setForm(p => {
      const next = { ...p, [k]: v };
      // Clear contact when account changes
      if (k === "account_id") next.contact_id = "";
      return next;
    });
    setErrors(p => ({ ...p, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Opportunity name is required";
    if (!form.account_id)  e.account_id = "Account is required";
    if (PROPOSAL_STAGES.has(form.stage) && !form.estimated_value)
      e.estimated_value = "Estimated value required for Proposal / Negotiation / Won stages";
    if (CLOSED_STAGES.has(form.stage) && !form.close_reason.trim())
      e.close_reason = "Close reason required for Closed Won / Lost";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_value: form.estimated_value ? Number(form.estimated_value) : null,
        contact_id: form.contact_id || undefined,
      };
      const { data } = await crmApi.createOpportunity(payload);
      toast.success(`Opportunity ${data.opportunity_number || data.name} created`);
      onCreated(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errs = {};
        detail.forEach(d => { errs[d.field] = d.message; });
        setErrors(errs);
      } else {
        toast.error(typeof detail === "string" ? detail : formatError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="bg-white rounded-xl border border-[#E5E5E5] w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#0176D3]" />
            <h2 className="text-base font-semibold text-[#181818]">New Opportunity</h2>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-[#706E6B]" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Opportunity Name <span className="text-rose-500">*</span></label>
            <input value={form.name} onChange={e => set("name", e.target.value)}
              placeholder="Wedding Celebration — Mehta Family" data-testid="opp-name"
              className={`${inp}${errors.name ? " border-rose-400" : ""}`} />
            {errors.name && <p data-testid="err-opp-name" className="text-xs text-rose-500 mt-1">{errors.name}</p>}
          </div>
          {/* Account */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Account <span className="text-rose-500">*</span></label>
            <select value={form.account_id} onChange={e => set("account_id", e.target.value)}
              data-testid="opp-account" className={`${inp}${errors.account_id ? " border-rose-400" : ""}`}>
              <option value="">— Select Account —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            {errors.account_id && <p data-testid="err-opp-account" className="text-xs text-rose-500 mt-1">{errors.account_id}</p>}
          </div>
          {/* Primary Contact (P1-10) */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Primary Contact</label>
            <select value={form.contact_id} onChange={e => set("contact_id", e.target.value)}
              disabled={!form.account_id || contactsLoading}
              data-testid="opp-contact"
              className={`${inp} disabled:opacity-50 disabled:cursor-not-allowed`}>
              <option value="">{form.account_id ? "— Select Contact (optional) —" : "— Select Account first —"}</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ""}</option>)}
            </select>
          </div>
          {/* Stage + Value */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Stage</label>
              <select value={form.stage} onChange={e => set("stage", e.target.value)}
                data-testid="opp-stage" className={inp}>
                {STAGES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">
                Estimated Value {PROPOSAL_STAGES.has(form.stage) && <span className="text-rose-500">*</span>}
              </label>
              <input type="number" min="0" value={form.estimated_value}
                onChange={e => set("estimated_value", e.target.value)}
                placeholder="₹ 0" data-testid="opp-value"
                className={`${inp}${errors.estimated_value ? " border-rose-400" : ""}`} />
              {errors.estimated_value && <p data-testid="err-opp-value" className="text-xs text-rose-500 mt-1">{errors.estimated_value}</p>}
            </div>
          </div>
          {CLOSED_STAGES.has(form.stage) && (
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Close Reason <span className="text-rose-500">*</span></label>
              <input value={form.close_reason} onChange={e => set("close_reason", e.target.value)}
                placeholder="Why was this won/lost?" data-testid="opp-close-reason"
                className={`${inp}${errors.close_reason ? " border-rose-400" : ""}`} />
              {errors.close_reason && <p data-testid="err-opp-close-reason" className="text-xs text-rose-500 mt-1">{errors.close_reason}</p>}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Event Type</label>
            <input value={form.event_type} onChange={e => set("event_type", e.target.value)}
              placeholder="WEDDING / CORPORATE / …" className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)}
              className={`${inp} resize-none`} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#706E6B]">Cancel</button>
          <button data-testid="opp-submit-btn" onClick={submit} disabled={saving}
            className="px-4 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#0060B0] disabled:opacity-50">
            {saving ? "Saving…" : "Create Opportunity"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Opportunity Modal (P1-10 contact + P1-13 owner) ─────────────────────
function EditOpportunityModal({ opp, currentUser, users, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:            opp.name            || "",
    account_id:      opp.account_id      || "",
    contact_id:      opp.contact_id      || "",
    stage:           opp.stage           || "PROSPECTING",
    estimated_value: opp.estimated_value || "",
    close_reason:    opp.close_reason    || "",
    event_type:      opp.event_type      || "",
    event_date:      opp.event_date      || "",
    notes:           opp.notes           || "",
    owner_id:        opp.owner_id        || "",
  });
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [accounts, setAccounts] = useState([]);
  const canReassign = hasCapability(currentUser, "crm.opportunity.reassign");

  const { contacts, loading: contactsLoading } = useAccountContacts(form.account_id);

  useEffect(() => {
    api.get("/crm/accounts", { params: { limit: 100 } })
      .then(r => setAccounts(r.data.results || []))
      .catch(() => {});
  }, []);

  const set = (k, v) => {
    setForm(p => {
      const next = { ...p, [k]: v };
      if (k === "account_id") next.contact_id = "";
      return next;
    });
    setErrors(p => ({ ...p, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Opportunity name is required";
    if (PROPOSAL_STAGES.has(form.stage) && !form.estimated_value)
      e.estimated_value = "Estimated value required for Proposal / Negotiation / Won stages";
    if (CLOSED_STAGES.has(form.stage) && !form.close_reason.trim())
      e.close_reason = "Close reason required for Closed Won / Lost";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name:            form.name,
        stage:           form.stage,
        estimated_value: form.estimated_value ? Number(form.estimated_value) : null,
        close_reason:    form.close_reason,
        event_type:      form.event_type,
        event_date:      form.event_date || null,
        notes:           form.notes,
        contact_id:      form.contact_id || "",
      };
      // Only include account_id if changed
      if (form.account_id !== opp.account_id) payload.account_id = form.account_id;
      // Only include owner_id if changed and allowed
      if (canReassign && form.owner_id && form.owner_id !== opp.owner_id) {
        payload.owner_id = form.owner_id;
      }
      const { data } = await crmApi.updateOpportunity(opp.id, payload);
      toast.success("Opportunity updated");
      onSaved(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errs = {};
        detail.forEach(d => { errs[d.field] = d.message; });
        setErrors(errs);
      } else {
        toast.error(typeof detail === "string" ? detail : formatError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="bg-white rounded-xl border border-[#E5E5E5] w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <Edit2 className="w-4 h-4 text-[#0176D3]" />
            <div>
              <h2 className="text-base font-semibold text-[#181818]">Edit Opportunity</h2>
              {opp.opportunity_number && (
                <p className="text-[10px] font-mono text-[#706E6B]">{opp.opportunity_number}</p>
              )}
            </div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-[#706E6B]" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Opportunity Name <span className="text-rose-500">*</span></label>
            <input value={form.name} onChange={e => set("name", e.target.value)}
              data-testid="edit-opp-name"
              className={`${inp}${errors.name ? " border-rose-400" : ""}`} />
            {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
          </div>
          {/* Account */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Account</label>
            <select value={form.account_id} onChange={e => set("account_id", e.target.value)}
              data-testid="edit-opp-account" className={inp}>
              <option value="">— Select Account —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          {/* Primary Contact (P1-10) */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Primary Contact</label>
            <select value={form.contact_id} onChange={e => set("contact_id", e.target.value)}
              disabled={!form.account_id || contactsLoading}
              data-testid="edit-opp-contact"
              className={`${inp} disabled:opacity-50 disabled:cursor-not-allowed`}>
              <option value="">{form.account_id ? "— No Contact —" : "— Select Account first —"}</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ""}</option>)}
            </select>
            {form.contact_id && opp.primary_contact_name && form.contact_id === opp.contact_id && (
              <p className="text-[10px] text-[#706E6B] mt-1 flex items-center gap-1">
                <UserCheck className="w-3 h-3" /> Currently: {opp.primary_contact_name}
              </p>
            )}
          </div>
          {/* Stage + Value */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Stage</label>
              <select value={form.stage} onChange={e => set("stage", e.target.value)}
                data-testid="edit-opp-stage" className={inp}>
                {STAGES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">
                Estimated Value {PROPOSAL_STAGES.has(form.stage) && <span className="text-rose-500">*</span>}
              </label>
              <input type="number" min="0" value={form.estimated_value}
                onChange={e => set("estimated_value", e.target.value)}
                placeholder="₹ 0" data-testid="edit-opp-value"
                className={`${inp}${errors.estimated_value ? " border-rose-400" : ""}`} />
              {errors.estimated_value && <p className="text-xs text-rose-500 mt-1">{errors.estimated_value}</p>}
            </div>
          </div>
          {CLOSED_STAGES.has(form.stage) && (
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Close Reason <span className="text-rose-500">*</span></label>
              <input value={form.close_reason} onChange={e => set("close_reason", e.target.value)}
                data-testid="edit-opp-close-reason"
                className={`${inp}${errors.close_reason ? " border-rose-400" : ""}`} />
              {errors.close_reason && <p className="text-xs text-rose-500 mt-1">{errors.close_reason}</p>}
            </div>
          )}
          {/* Owner Picker (P1-13) — visible only if user has crm.opportunity.reassign */}
          {canReassign && (
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1 flex items-center gap-1">
                <User className="w-3 h-3" /> Owner
              </label>
              <select value={form.owner_id} onChange={e => set("owner_id", e.target.value)}
                data-testid="edit-opp-owner" className={inp}>
                <option value="">— Current: {opp.owner_name || "Unassigned"} —</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.role?.replace(/_/g, " ")})</option>
                ))}
              </select>
            </div>
          )}
          {!canReassign && opp.owner_name && (
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1 flex items-center gap-1">
                <User className="w-3 h-3" /> Owner
              </label>
              <p className="text-sm text-[#444] px-3 py-2 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg">{opp.owner_name}</p>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Event Type</label>
            <input value={form.event_type} onChange={e => set("event_type", e.target.value)}
              placeholder="WEDDING / CORPORATE / …" className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)}
              data-testid="edit-opp-notes" className={`${inp} resize-none`} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#706E6B]">Cancel</button>
          <button data-testid="edit-opp-submit-btn" onClick={submit} disabled={saving}
            className="px-4 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#0060B0] disabled:opacity-50">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function CRMOpportunities() {
  const { user } = useAuth();
  const navigate = useNavigate();  // kept for any future nav usage
  const moduleLabel = useLabel("crm.module.opportunities", "Opportunities");

  // CRM-UX-01 Stage 03: personalized columns
  const [visibleCols,   setVisibleCols]   = useState(DEFAULT_OPPS_COLS);
  const [showColPicker, setShowColPicker] = useState(false);
  const [pendingCols,   setPendingCols]   = useState(DEFAULT_OPPS_COLS);
  const [colPrefSaving, setColPrefSaving] = useState(false);

  const [opps,    setOpps]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [stage,   setStage]   = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [page,    setPage]    = useState(0);
  const [stages,  setStages]  = useState([]);
  const [users,   setUsers]   = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editOpp,    setEditOpp]    = useState(null);
  const [planningId, setPlanningId] = useState(null); // CORE-04: tracks which opp is being started

  // CRM-UX-03: scope default — RM/operational roles start with "My Opps"; supervisors see "All"
  const _supervisorRoles = ["BUSINESS_OWNER", "CRM_MANAGER", "TENANT_ADMIN", "PLATFORM_ADMIN"];
  const defaultScope = _supervisorRoles.includes(user?.role) ? "all" : "my";
  const [scope, setScope] = useState(defaultScope);

  const canReassign = hasCapability(user, "crm.opportunity.reassign");
  const canConvert  = hasCapability(user, "crm.lead.convert");

  // Load users list once (for owner filter + owner picker in EditModal)
  useEffect(() => {
    api.get("/engage/users")
      .then(r => setUsers(r.data || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/opportunities", {
        params: {
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
          ...(search   && { search }),
          ...(stage    && { stage }),
          ...(ownerId  && { owner_id: ownerId }),
          // CRM-UX-03: scope filter (only sent when no explicit ownerId filter applied)
          ...(!ownerId && { scope }),
        },
      });
      setOpps(data.results || []);
      setTotal(data.total || 0);
      if ((data.results || []).length > 0) {
        const uniqueStages = [...new Set((data.results || []).map(o => o.stage).filter(Boolean))];
        setStages(uniqueStages);
      }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [page, search, stage, ownerId, scope]);

  useEffect(() => {
    const t = setTimeout(() => load(), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  // CRM-UX-01 Stage 03: load saved column preferences on mount
  useEffect(() => {
    userPrefsApi.getListView("opportunities")
      .then(({ data }) => {
        if (data?.columns?.length) {
          setVisibleCols(data.columns);
          setPendingCols(data.columns);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // CORE-04: Start Event Planning from a CLOSED_WON Opportunity
  const handleStartEventPlanning = async (opp, e) => {
    e.stopPropagation();
    if (planningId) return;
    setPlanningId(opp.id);
    try {
      const { data } = await crmApi.startEventPlanningFromOpp(opp.id);
      if (data.idempotent) {
        toast.info(`Event ${data.event_number} already exists for this opportunity.`);
      } else {
        toast.success(`Event ${data.event_number} created. Opening Event Brief…`);
      }
      navigate(`/imagine/events/${data.event_id}`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setPlanningId(null);
    }
  };

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto space-y-5">
      {showCreate && (
        <CreateOpportunityModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {editOpp && (
        <EditOpportunityModal
          opp={editOpp}
          currentUser={user}
          users={users}
          onClose={() => setEditOpp(null)}
          onSaved={() => { setEditOpp(null); load(); }}
        />
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-[#0176D3]" />
          <h1 className="text-xl font-bold text-[#181818] font-sans" data-testid="opps-page-heading">{moduleLabel}</h1>
          <span className="text-xs font-mono text-[#706E6B] bg-[#F3F3F3] border border-[#E5E5E5] px-2 py-0.5 rounded">
            {total} total
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* CRM-UX-03: Scope toggle */}
          <div
            data-testid="opps-scope-toggle"
            className="flex items-center bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg overflow-hidden text-xs"
          >
            <button
              data-testid="opps-scope-my"
              onClick={() => { setScope("my"); setPage(0); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors font-medium ${
                scope === "my"
                  ? "bg-[#0176D3] text-white"
                  : "text-[#444444] hover:text-[#0176D3]"
              }`}
            >
              <Users className="w-3 h-3" /> My Opps
            </button>
            <button
              data-testid="opps-scope-all"
              onClick={() => { setScope("all"); setPage(0); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors font-medium ${
                scope === "all"
                  ? "bg-[#0176D3] text-white"
                  : "text-[#444444] hover:text-[#0176D3]"
              }`}
            >
              {user?.role === "BUSINESS_OWNER" && <Shield className="w-3 h-3" />}
              All {user?.role === "BUSINESS_OWNER" ? "Authorized" : ""}
            </button>
          </div>
          <button data-testid="new-opp-btn" onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-xs text-[#0176D3] hover:bg-[#EEF4FF] transition-colors font-medium">
            <Plus className="w-3.5 h-3.5" /> New Opportunity
          </button>

          {/* CRM-UX-01 Stage 03: Column picker */}
          <div className="relative">
            <button
              data-testid="opps-column-picker-btn"
              onClick={() => { setShowColPicker(v => !v); setPendingCols(visibleCols); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E5E5] rounded-lg text-xs text-[#706E6B] hover:text-[#181818] hover:border-slate-500 transition-colors"
            >
              <Columns className="w-3.5 h-3.5" /> Columns
            </button>
            {showColPicker && (
              <div className="absolute right-0 top-8 z-40 bg-white border border-[#E5E5E5] rounded-xl shadow-lg p-4 w-52 space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#706E6B] mb-2">Visible Columns</p>
                {OPPS_COL_REGISTRY.map(col => (
                  <label key={col.id} className="flex items-center gap-2 text-xs text-[#444444] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`opps-col-toggle-${col.id}`}
                      checked={pendingCols.includes(col.id)}
                      disabled={col.alwaysVisible}
                      onChange={e => {
                        setPendingCols(prev =>
                          e.target.checked
                            ? [...prev, col.id]
                            : prev.filter(c => c !== col.id)
                        );
                      }}
                      className="rounded border-[#E5E5E5] text-[#0176D3] disabled:opacity-40"
                    />
                    <span className={col.alwaysVisible ? "text-[#706E6B]" : ""}>{col.label}</span>
                    {col.alwaysVisible && <span className="text-[9px] text-[#C9C9C9] ml-auto">fixed</span>}
                  </label>
                ))}
                <div className="flex gap-2 pt-2 border-t border-[#E5E5E5] mt-2">
                  <button
                    data-testid="opps-save-column-prefs-btn"
                    disabled={colPrefSaving}
                    onClick={async () => {
                      setColPrefSaving(true);
                      try {
                        await userPrefsApi.saveListView("opportunities", { columns: pendingCols });
                        setVisibleCols(pendingCols);
                        setShowColPicker(false);
                      } catch { /* silent */ }
                      finally { setColPrefSaving(false); }
                    }}
                    className="flex-1 px-2 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded text-[10px] text-[#0176D3] font-medium"
                  >
                    {colPrefSaving ? "Saving…" : "Save"}
                  </button>
                  <button
                    data-testid="opps-restore-default-cols-btn"
                    onClick={async () => {
                      try { await userPrefsApi.deleteListView("opportunities"); } catch { /* silent */ }
                      setVisibleCols(DEFAULT_OPPS_COLS);
                      setPendingCols(DEFAULT_OPPS_COLS);
                      setShowColPicker(false);
                    }}
                    className="flex-1 px-2 py-1.5 border border-[#E5E5E5] rounded text-[10px] text-[#706E6B] hover:border-slate-500"
                  >
                    Default
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-40">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search opportunities…"
              data-testid="opps-search"
              className="w-full pl-8 pr-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]/30"
            />
          </div>
          {stages.length > 0 && (
            <select value={stage} onChange={e => { setStage(e.target.value); setPage(0); }}
              data-testid="opps-filter-stage"
              className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#444444] focus:outline-none">
              <option value="">All Stages</option>
              {stages.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          )}
          {/* P1-13: Owner filter — visible only if user has crm.opportunity.reassign */}
          {canReassign && users.length > 0 && (
            <select value={ownerId} onChange={e => { setOwnerId(e.target.value); setPage(0); }}
              data-testid="opps-filter-owner"
              className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#444444] focus:outline-none">
              <option value="">All Owners</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid="opportunities-list">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#0176D3]" />
          </div>
        ) : opps.length === 0 ? (
          <EmptyState
            icon={TrendingUp}
            title="No Opportunities yet"
            note={
              search || stage || ownerId
                ? "No opportunities match the current filters."
                : "Opportunities will appear here when Leads are converted to active sales opportunities."
            }
          />
        ) : (
          <>
            {/* ── Mobile card list ──────────────────────────────────────────── */}
            <div className="sm:hidden divide-y divide-[#E5E5E5]">
              {opps.map(opp => (
                <div
                  key={opp.id}
                  data-testid="opportunity-row"
                  onClick={() => setEditOpp(opp)}
                  className="flex items-start gap-3 px-4 py-3.5 cursor-pointer hover:bg-[#EEF4FF] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {opp.stage && (
                        <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${STAGE_COLORS[opp.stage] || "text-[#706E6B] bg-slate-400/10 border-slate-400/20"}`}>
                          {opp.stage.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-[#181818] truncate">{opp.name || "—"}</p>
                    <p className="text-xs text-[#706E6B] mt-0.5 truncate">
                      {opp.account_name || "—"}
                      {opp.primary_contact_name ? ` · ${opp.primary_contact_name}` : ""}
                    </p>
                    {opp.owner_name && (
                      <p className="text-[10px] text-[#706E6B] mt-0.5 flex items-center gap-1">
                        <User className="w-2.5 h-2.5" />{opp.owner_name}
                      </p>
                    )}
                    {/* CORE-04: Start Event Planning for CLOSED_WON on mobile */}
                    {canConvert && opp.stage === "CLOSED_WON" && !opp.related_event_id && (
                      <button
                        data-testid="start-event-planning-mobile-btn"
                        onClick={(e) => handleStartEventPlanning(opp, e)}
                        disabled={planningId === opp.id}
                        className="mt-1.5 flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                      >
                        {planningId === opp.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : null}
                        Start Event Planning
                      </button>
                    )}
                    {opp.related_event_id && (
                      <button
                        data-testid="view-event-mobile-btn"
                        onClick={(e) => { e.stopPropagation(); navigate(`/imagine/events/${opp.related_event_id}`); }}
                        className="mt-1.5 flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-[#EEF4FF] text-[#0176D3] rounded hover:bg-[#D8EAF8] transition-colors"
                      >
                        View Event
                      </button>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#706E6B] flex-shrink-0 mt-1" />
                </div>
              ))}
            </div>
            {/* ── Desktop table ──────────────────────────────────────────────── */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E5E5] bg-white">
                    {OPPS_COL_REGISTRY.filter(c => visibleCols.includes(c.id)).map(c => (
                      <Th key={c.id}>{c.label}</Th>
                    ))}
                    <th className="px-4 py-3 w-8" />
                  </tr>
                </thead>
                <tbody>
                  {opps.map((opp, idx) => (
                    <tr
                      key={opp.id}
                      data-testid="opportunity-row"
                      onClick={() => setEditOpp(opp)}
                      className={`border-b border-[#E5E5E5] cursor-pointer hover:bg-[#EEF4FF] transition-colors ${idx % 2 !== 0 ? "bg-white/30" : ""}`}
                    >
                      {visibleCols.includes("name") && (
                        <td className="px-4 py-3">
                          <p className="text-[#181818] font-sans font-medium">{opp.name || "—"}</p>
                          {opp.opportunity_number && <p className="text-[10px] font-mono text-[#706E6B]">{opp.opportunity_number}</p>}
                        </td>
                      )}
                      {visibleCols.includes("account_name") && (
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1 text-xs text-[#706E6B]">
                            <Building2 className="w-3 h-3" />{opp.account_name || "—"}
                          </span>
                        </td>
                      )}
                      {visibleCols.includes("contact") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B]">{opp.primary_contact_name || <span className="text-[#C9C9C9]">—</span>}</span>
                        </td>
                      )}
                      {visibleCols.includes("stage") && (
                        <td className="px-4 py-3">
                          {opp.stage ? (
                            <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STAGE_COLORS[opp.stage] || "text-[#706E6B] bg-slate-400/10 border-slate-400/20"}`}>
                              {opp.stage.replace(/_/g, " ")}
                            </span>
                          ) : <span className="text-[#706E6B] text-xs">—</span>}
                        </td>
                      )}
                      {visibleCols.includes("owner") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B] flex items-center gap-1">
                            {opp.owner_name ? <><User className="w-2.5 h-2.5" />{opp.owner_name}</> : "—"}
                          </span>
                        </td>
                      )}
                      {visibleCols.includes("budget_range") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B]">
                            {opp.budget_range || (opp.estimated_value ? `₹${Number(opp.estimated_value).toLocaleString()}` : "—")}
                          </span>
                        </td>
                      )}
                      {visibleCols.includes("event_type") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B]">{opp.event_type || "—"}</span>
                        </td>
                      )}
                      {visibleCols.includes("event_date") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B]">{opp.event_date ? new Date(opp.event_date).toLocaleDateString() : "—"}</span>
                        </td>
                      )}
                      {visibleCols.includes("estimated_value") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B]">{opp.estimated_value ? `₹${Number(opp.estimated_value).toLocaleString()}` : "—"}</span>
                        </td>
                      )}
                      {visibleCols.includes("source_lead") && (
                        <td className="px-4 py-3">
                          <span className="text-xs font-mono text-[#706E6B]">{opp.source_lead_number || "—"}</span>
                        </td>
                      )}
                      <td className="px-4 py-3 text-right">
                        {canConvert && opp.stage === "CLOSED_WON" && !opp.related_event_id ? (
                          <button
                            data-testid="start-event-planning-btn"
                            onClick={(e) => handleStartEventPlanning(opp, e)}
                            disabled={planningId === opp.id}
                            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:opacity-50 transition-colors whitespace-nowrap"
                          >
                            {planningId === opp.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : null}
                            Start Event Planning
                          </button>
                        ) : opp.related_event_id ? (
                          <button
                            data-testid="view-event-btn"
                            onClick={(e) => { e.stopPropagation(); navigate(`/imagine/events/${opp.related_event_id}`); }}
                            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium bg-[#EEF4FF] text-[#0176D3] rounded hover:bg-[#D8EAF8] transition-colors whitespace-nowrap"
                          >
                            View Event
                          </button>
                        ) : (
                          <Edit2 className="w-3.5 h-3.5 text-[#706E6B] inline-block" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} total={total} size={PAGE_SIZE} onPage={setPage} />
          </>
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`text-left px-4 py-3 text-xs font-mono text-[#706E6B] uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function EmptyState({ icon: Icon, title, note }) {
  return (
    <div className="py-16 text-center">
      <Icon className="w-8 h-8 text-[#444444] mx-auto mb-3" />
      <p className="text-sm text-[#706E6B] font-medium">{title}</p>
      {note && <p className="text-xs text-[#706E6B] mt-1 max-w-sm mx-auto">{note}</p>}
    </div>
  );
}

function Pagination({ page, total, size, onPage }) {
  if (total <= size) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E5E5]">
      <p className="text-xs text-[#706E6B]">
        Showing {page * size + 1}–{Math.min((page + 1) * size, total)} of {total}
      </p>
      <div className="flex gap-2">
        <button disabled={page === 0} onClick={() => onPage(p => p - 1)}
          className="px-3 py-1 text-xs border border-[#E5E5E5] rounded text-[#706E6B] hover:text-[#181818] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          Prev
        </button>
        <button disabled={(page + 1) * size >= total} onClick={() => onPage(p => p + 1)}
          className="px-3 py-1 text-xs border border-[#E5E5E5] rounded text-[#706E6B] hover:text-[#181818] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          Next
        </button>
      </div>
    </div>
  );
}
